// src/shared/components/ui/DateRangePicker.tsx
import React, { useState } from 'react'
import 'react-day-picker/dist/style.css'
import {
  DayPicker,
  type DateRange,
  type ModifiersClassNames,
} from 'react-day-picker'
import {
  format, startOfDay, endOfDay,
  addDays, startOfWeek, startOfMonth,
} from 'date-fns'
import { Popover, PopoverTrigger, PopoverContent } from './popover'
import { Button } from './button'

/* ——————————————————————————————————————————— colours — */
const UI = {
  bgPanel:        'bg-neutral-800',
  bgHover:        'bg-neutral-700',
  fgPreset:       'text-neutral-300',
  fgPresetActive: 'text-white',
}

/* ————————————————————————————— range colours ———— */
const MODIFIERS: ModifiersClassNames = {
  selected:     'bg-neutral-500 text-white rounded-full',
  range_start:  'bg-neutral-500 text-white rounded-full',
  range_middle: 'bg-neutral-500/30 text-white',
  range_end:    'bg-neutral-500 text-white rounded-full',
}

/* ————————————————————————————— presets ———— */
export type RangeStrings = { from: string; to: string }
type Preset = { label: string; get: () => RangeStrings }

const now       = new Date()
const endToday  = endOfDay(now)
const iso       = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm:ss")

const PRESETS: Preset[] = [
  { label: 'Today',         get: () => ({ from: iso(startOfDay(now)),                to: iso(endToday) }) },
  { label: 'Week to date',  get: () => ({ from: iso(startOfDay(startOfWeek(now,{weekStartsOn:1}))), to: iso(endToday) }) },
  { label: 'Month to date', get: () => ({ from: iso(startOfDay(startOfMonth(now))),  to: iso(endToday) }) },
  { label: 'Last 7 days',   get: () => ({ from: iso(startOfDay(addDays(now,-6))),    to: iso(endToday) }) },
  { label: 'Last 14 days',  get: () => ({ from: iso(startOfDay(addDays(now,-13))),   to: iso(endToday) }) },
  { label: 'Last 30 days',  get: () => ({ from: iso(startOfDay(addDays(now,-29))),   to: iso(endToday) }) },
]

/* ————————————————————————— component ——————— */
interface Props {
  value?: RangeStrings
  onChange: (r: RangeStrings | undefined) => void
  placeholder?: string
}

export default function DateRangePicker({
  value, onChange, placeholder = 'Select date range',
}: Props) {
  const [open,  setOpen]  = useState(false)
  const [draft, setDraft] = useState<RangeStrings | undefined>(value)

  const commit = (r?: DateRange) => {
    if (r?.from && r.to) {
      const sel = {
        from: iso(startOfDay(r.from)),
        to:   iso(endOfDay(r.to)),
      }
      setDraft(sel)
      onChange(sel)
      setOpen(false)
    }
  }

  const applyPreset = (p: Preset) => {
    const sel = p.get()
    setDraft(sel)
    onChange(sel)
    setOpen(false)
  }

  const selectedRange = draft
    ? { from: new Date(draft.from), to: new Date(draft.to) }
    : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
        {draft
          ? draft.from.slice(0,10) === draft.to.slice(0,10)
              ? draft.from.slice(0,10)
              : `${draft.from.slice(0,10)} → ${draft.to.slice(0,10)}`
          : placeholder}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className={`flex ${UI.bgPanel} rounded-xl p-0 shadow-lg`}>
        {/* presets */}
        <div className="flex flex-col w-44 border-r border-neutral-700">
          {PRESETS.map(p => {
            const a = p.get()
            const active = draft?.from === a.from && draft?.to === a.to
            return (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                className={`
                  px-4 py-2 text-sm text-left
                  ${active ? UI.fgPresetActive : UI.fgPreset}
                  hover:${UI.bgHover}
                `}
              >
                {p.label}
              </button>
            )
          })}
        </div>

        {/* calendar */}
        <div className="p-4">
          <DayPicker
            mode="range"
            numberOfMonths={2}
            selected={selectedRange}
            onSelect={commit}
            modifiersClassNames={MODIFIERS}
            showOutsideDays
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
